<template>
  <div v-if="countriesFetchState === 'error'" class="pt-2 pb-4">
    <p class="text-center mb-2">
      {{ t('general/error/text') }}
    </p>
    <button
      class="btn btn-lg btn-primary w-100"
      type="button"
      @click="() => fetchCountries"
    >
      {{ t('general/retry') }}
    </button>
  </div>

  <form v-else @submit="handleRegisterSubmit">
    <p class="mb-2">
      <strong class="fs-2">
        {{ t('register/form/header') }}
      </strong>
    </p>

    <form-text
      v-model="formData.email"
      autocomplete="email"
      type="email"
      :label="`${t('frontend/email')}:*`"
      :error="getErrorMessage(errorFields.email, 'frontend/email')"
      :input-id="'register__email'"
    />
    <div class="row">
      <form-text
        v-model="formData.firstname"
        class="col-12 col-sm-6 mt-6"
        autocomplete="given-name"
        :label="`${t('frontend/first-name')}:*`"
        :error="getErrorMessage(errorFields.firstname, 'frontend/first-name')"
        :input-id="'register__first-name'"
      />
      <form-text
        v-model="formData.lastname"
        class="col-12 col-sm-6 mt-6"
        autocomplete="family-name"
        :label="`${t('frontend/last-name')}:*`"
        :error="getErrorMessage(errorFields.lastname, 'frontend/last-name')"
        :input-id="'register__last-name'"
      />
      <form-select
        v-model="formData.country"
        class="register__form-select mt-6 col-12 col-sm-6"
        value-key="code"
        label-key="name"
        :label="`${t('frontend/country')}:*`"
        :data="availableCountries"
        :filter-items-fn="handleFilterCountries"
        :input-id="'register__country'"
        :disabled="availableCountries.length === 0"
      />

      <template v-if="isCountryZipRequired">
        <form-text
          v-model="formData.postalCode"
          class="col-6 mt-6"
          autocomplete="postal-code"
          :label="`${t('frontend/postal-code')}:*`"
          :error="
            t(errorFields.postalCode, { field: t('frontend/postal-code') })
          "
          :input-id="'register__postal-code'"
        />
      </template>

      <template v-if="isCountryFullAddressRequired">
        <form-text
          v-model="formData.street"
          class="col-12 mt-6"
          autocomplete="address-line1"
          :label="`${t('frontend/address')}:*`"
          :error="getErrorMessage(errorFields.street, 'frontend/address')"
          :input-id="'register__address'"
        />
        <form-text
          v-model="formData.postalCode"
          class="col-12 col-sm-6 mt-6"
          autocomplete="postal-code"
          :label="`${t('frontend/postal-code')}:*`"
          :error="
            getErrorMessage(errorFields.postalCode, 'frontend/postal-code')
          "
          :input-id="'register__zipcode'"
        />
        <form-text
          v-model="formData.locality"
          class="col-12 col-sm-6 mt-6"
          autocomplete="address-level2"
          :label="`${t('frontend/city')}:*`"
          :error="getErrorMessage(errorFields.locality, 'frontend/city')"
          :input-id="'register__city'"
        />
      </template>
    </div>

    <register-form-input-password
      v-model="formData.password"
      class="mt-6"
      :error-message="errorFields.password"
    />

    <form-text
      v-model="formData.passwordRepeat"
      type="password"
      class="mt-6"
      autocomplete="new-password"
      :label="`${t('frontend/repeat-password')}:*`"
      :error="
        getErrorMessage(errorFields.passwordRepeat, 'frontend/repeat-password')
      "
      :input-id="'register__repeat-password'"
      :debounce="300"
    />

    <i18n-t keypath="register/form/conditions" tag="p" class="mt-6 fs-tiny">
      <a class="fw-bold" :href="`/${region}/terms`"
        >{{ t('meta/terms_of_use') }}
      </a>
      <a class="fw-bold" :href="`/${region}/privacy`">{{
        t('meta/privacy_policy')
      }}</a>
    </i18n-t>

    <div class="d-flex">
      <form-switch v-model="formData.newsletter" class="me-4" />
      <span class="fs-tiny">
        {{ t('frontend/sign-up-for-our-newsletter') }}
      </span>
    </div>

    <button
      class="btn btn-lg btn-primary my-6 w-100"
      type="submit"
      :disabled="isSubmitting || !isFormValid"
    >
      {{ t('register/page/header/create_account') }}
    </button>
  </form>
</template>

<script lang="ts" setup>
import useCountries from './features/useCountries';
import useSubmitData from './features/useSubmitData';
import type { RegisterUserFormData } from './types';

const { t, locale } = useI18n();

const formData = reactive<RegisterUserFormData>({
  email: '',
  firstname: '',
  lastname: '',
  country: 'DE',
  password: '',
  passwordRepeat: '',
  newsletter: false,
  postalCode: '',
  street: '',
  locality: '',
});

const {
  availableCountries,
  isCountryFullAddressRequired,
  isCountryZipRequired,
  countriesFetchState,
  fetchCountries,
  handleFilterCountries,
} = await useCountries(formData);

const { isSubmitting, errorFields, handleRegisterSubmit, isFormValid } =
  useSubmitData(formData, isCountryZipRequired, isCountryFullAddressRequired);

const currencyStore = useCurrencyStore();
const currentCurrency = computed<string>(() => currencyStore.currency);

// for creation for terms and privacy links
const region = ref<string>(
  getLegacyLocalePrefix(locale.value, currentCurrency.value),
);

watch(
  () => locale.value,
  () => {
    region.value = getLegacyLocalePrefix(locale.value, currentCurrency.value);
  },
);

const getErrorMessage = (
  errorField: (typeof errorFields.value)[keyof typeof errorFields.value],
  translateParamKey: string,
) => {
  return errorField != null && errorField.length > 0
    ? t(errorField, { field: t(translateParamKey) })
    : '';
};
</script>
